<template>
  <div class="container">
    <div class="row">
      <div class="col">
        <div class="d-flex justify-content-between align-items-center">
          <h1 class="mt-3">Manage Categorías</h1>
          <router-link
            class="btn btn-dark"
            :to="{ name: 'BookEdit', params: { productId: 0 } }"
          >
            Add Product
          </router-link>
        </div>
        <hr />

        <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

        <table v-if="ready" class="table table-striped table-compact align-middle">
          <thead class="table-light">
            <tr>
              <th style="width: 80px;">ID</th>
              <th>Nombre</th>
              <th style="width: 220px;">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="3" class="text-muted">Cargando categorías…</td>
            </tr>

            <tr v-for="c in categories" :key="c.id">
              <td>{{ c.id }}</td>

              <!-- Nombre: editable -->
              <td>
                <div v-if="c._editing">
                  <input
                    v-model.trim="c._editName"
                    type="text"
                    class="form-control form-control-sm"
                    :disabled="savingId === c.id"
                    @keyup.enter="saveCategory(c)"
                  />
                  <small v-if="c._editError" class="text-danger">{{ c._editError }}</small>
                </div>
                <div v-else>{{ c.name }}</div>
              </td>

              <!-- Acciones -->
              <td class="d-flex gap-2">
                <template v-if="c._editing">
                  <button
                    class="btn btn-sm btn-success"
                    :disabled="savingId === c.id || !c._editName"
                    @click="saveCategory(c)"
                  >
                    Guardar
                  </button>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="savingId === c.id"
                    @click="cancelEdit(c)"
                  >
                    Cancelar
                  </button>
                </template>
                <template v-else>
                  <button
                    class="btn btn-sm btn-outline-primary"
                    :disabled="savingId || deletingId === c.id"
                    @click="startEdit(c)"
                  >
                    Editar
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    :disabled="deletingId === c.id || savingId"
                    @click="deleteCategory(c)"
                  >
                    Eliminar
                  </button>
                </template>
              </td>
            </tr>

            <tr v-if="!loading && categories.length === 0">
              <td colspan="3" class="text-muted">No hay categorías.</td>
            </tr>
          </tbody>
        </table>

        <!-- Preview solo etiquetas -->
        <hr />
        <h5 class="mb-2">Categorías (preview)</h5>
        <div v-if="categories.length" class="d-flex flex-wrap gap-2">
          <span
            v-for="(c, i) in categories"
            :key="(c.name || '').toLowerCase() + '-' + i"
            :class="['badge', badgeClass(i), 'px-3', 'py-2']"
          >
            {{ c.name }}
          </span>
        </div>
        <small v-else class="text-muted">No hay categorías seleccionadas.</small>
      </div>
    </div>
  </div>
</template>

<script>
import Security from './security';

export default {
  name: "ManageCategories",
  data() {
    return {
      categories: [],   // [{ id, name, _editing, _editName, _editError }]
      ready: false,
      loading: false,
      savingId: null,
      deletingId: null,
      error: "",
    };
  },
  mounted() {
    this.loadCategories();
  },
  methods: {
    async loadCategories() {
      this.loading = true;
      this.error = "";
      try {
        const res = await fetch(
          process.env.VUE_APP_API_URL + "/api/product/categories/",
          Security.requestOptions({}, "GET")
        );
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`GET ${res.status}: ${txt.slice(0, 200)}`);
        }
        const data = await res.json();

        this.categories = Array.isArray(data)
          ? data.map((c) => ({
              id: c.id,
              name: c.name,
              _editing: false,
              _editName: c.name,
              _editError: "",
            }))
          : [];
        this.ready = true;
      } catch (e) {
        console.error("[loadCategories]", e);
        this.error = "No se pudieron cargar las categorías.";
      } finally {
        this.loading = false;
      }
    },

    startEdit(c) {
      c._editing = true;
      c._editName = c.name;
      c._editError = "";
    },
    cancelEdit(c) {
      c._editing = false;
      c._editName = c.name;
      c._editError = "";
    },

    async saveCategory(c) {
      const newName = (c._editName || "").trim();
      if (!newName) {
        c._editError = "El nombre no puede estar vacío.";
        return;
      }
      this.savingId = c.id;
      c._editError = "";
      try {
        const res = await fetch(
          process.env.VUE_APP_API_URL + `/api/product/categories/${c.id}/`,
          Security.requestOptions({ name: newName }, "PUT")
        );
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`PUT ${res.status}: ${txt.slice(0, 200)}`);
        }

        try {
          const updated = await res.json();
          c.name = updated.name || newName;
        } catch {
          c.name = newName;
        }
        c._editing = false;
      } catch (e) {
        console.error("[saveCategory]", e);
        c._editError = "No se pudo guardar. Verifica el servidor/credenciales.";
      } finally {
        this.savingId = null;
      }
    },

    async deleteCategory(c) {
      if (!confirm(`¿Eliminar la categoría "${c.name}" (ID ${c.id})?`)) return;

      this.deletingId = c.id;
      try {
        const res = await fetch(
          process.env.VUE_APP_API_URL + `/api/product/categories/${c.id}/`,
          Security.requestOptions({}, "DELETE")
        );
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`DELETE ${res.status}: ${txt.slice(0, 200)}`);
        }
        this.categories = this.categories.filter(x => x.id !== c.id);
      } catch (e) {
        console.error("[deleteCategory]", e);
        this.error = "No se pudo eliminar la categoría.";
      } finally {
        this.deletingId = null;
      }
    },

    badgeClass(i) {
      const palette = [
        "bg-primary text-white",
        "bg-success text-white",
        "bg-info text-dark",
        "bg-warning text-dark",
        "bg-danger text-white",
        "bg-secondary text-white",
        "bg-dark text-white",
      ];
      return palette[i % palette.length];
    },
  },
};
</script>
